import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import type { FundAsset } from "@/types";
import { useCashflows, useUpsertCashflows, useToggleCashflowReceived } from "@/hooks/useCashflows";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const CURRENT_YEAR = new Date().getFullYear();

interface Props {
  asset: FundAsset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readOnly?: boolean;
}

function fmtMillions(v: number) {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
}

export function AssetCashflowDialog({ asset, open, onOpenChange, readOnly }: Props) {
  const { data: cashflows } = useCashflows(asset?.id);
  const upsert = useUpsertCashflows();
  const toggleReceived = useToggleCashflowReceived();
  const [rows, setRows] = useState<Array<{ id?: string; year: string; amount: string; received: boolean }>>([]);

  useEffect(() => {
    if (cashflows) {
      setRows(
        [...cashflows]
          .sort((a, b) => a.year - b.year)
          .map((c) => ({
            id: c.id,
            year: String(c.year),
            amount: c.amount != null ? String(c.amount) : "",
            received: c.received ?? false,
          }))
      );
    } else {
      setRows([]);
    }
  }, [cashflows, open]);

  const addRow = () => setRows((r) => [...r, { year: "", amount: "", received: false }]);
  const removeRow = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: "year" | "amount", v: string) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: v } : row)));

  const handleSave = async () => {
    if (!asset) return;
    const payload = rows
      .filter((r) => r.year.trim())
      .map((r) => ({
        year: parseInt(r.year),
        amount: r.amount === "" ? null : parseFloat(r.amount),
        received: r.received,
      }));
    try {
      await upsert.mutateAsync({ fund_asset_id: asset.id, cashflows: payload });
      toast.success("Cashflows saved");
      onOpenChange(false);
    } catch {
      toast.error("Error saving cashflows");
    }
  };

  // Chart data: only valid rows with year + amount
  const chartData = useMemo(() => {
    return rows
      .filter((r) => r.year.trim() && r.amount.trim())
      .map((r) => ({ year: parseInt(r.year), amount: parseFloat(r.amount) || 0 }))
      .sort((a, b) => a.year - b.year);
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{asset?.asset_name} — Cashflow</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-2">
          Fill in the annual values. Years before {CURRENT_YEAR} will be summed in{" "}
          <span className="font-medium">Capital Received</span>.
        </p>

        {/* Live preview chart */}
        {chartData.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground font-medium">Preview</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  className="fill-muted-foreground"
                  tickFormatter={fmtMillions}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  formatter={(value: number) =>
                    new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(value)
                  }
                  labelFormatter={(label) => `Year ${label}`}
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                />
                <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.year}
                      fill={
                        entry.year < CURRENT_YEAR
                          ? "hsl(var(--muted-foreground))"
                          : "hsl(var(--primary))"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Rows */}
        <div className="space-y-2">
          <div className="grid grid-cols-[80px_1fr_80px_32px_auto] gap-2 text-xs font-medium text-muted-foreground px-1">
            <span>Year</span>
            <span>Value (BRL)</span>
            <span />
            <span className="text-center">Rec.</span>
            <span />
          </div>
          {rows.map((row, i) => {
            const yr = parseInt(row.year);
            const isPast = !isNaN(yr) && yr < CURRENT_YEAR;
            const isCurrent = yr === CURRENT_YEAR;
            return (
              <div key={i} className="grid grid-cols-[80px_1fr_80px_32px_auto] gap-2 items-center">
                <Input
                  type="number"
                  placeholder="2025"
                  value={row.year}
                  onChange={(e) => updateRow(i, "year", e.target.value)}
                  className="h-8 text-sm"
                  readOnly={readOnly}
                />
                <Input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={row.amount}
                  onChange={(e) => updateRow(i, "amount", e.target.value)}
                  className="h-8 text-sm"
                  readOnly={readOnly}
                />
                <span
                  className={`text-xs text-right ${
                    isPast ? "text-muted-foreground" : "text-primary/70"
                  }`}
                >
                  {row.year ? (isPast ? "past" : isCurrent ? "current" : "future") : ""}
                </span>
                {isCurrent ? (
                  <div className="flex justify-center">
                    <Checkbox
                      checked={row.received}
                      disabled={readOnly}
                      onCheckedChange={async (checked) => {
                        setRows((r) =>
                          r.map((x, idx) => idx === i ? { ...x, received: !!checked } : x)
                        );
                        if (row.id) {
                          await toggleReceived.mutateAsync({ id: row.id, received: !!checked });
                        }
                      }}
                    />
                  </div>
                ) : (
                  <span />
                )}
                {!readOnly ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(i)}
                    className="h-8 w-8"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                ) : (
                  <span className="h-8 w-8" />
                )}
              </div>
            );
          })}
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2">
              {readOnly ? "No cashflows recorded." : "Add years below."}
            </p>
          )}
        </div>

        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRow}
            className="w-full gap-2"
          >
            <Plus className="h-3.5 w-3.5" /> Add year
          </Button>
        )}

        {!readOnly && (
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={upsert.isPending}>
              {upsert.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
